import { ArrowDownToLine, Bot, Shapes, Sparkles } from 'lucide-react';
import { TASK_AI_BUILDER_NAME, type TaskLabel, type TaskMember, type TaskType } from '@shared/types/tasks';
import { upstreamBadgeText, type TaskUpstream } from '@shared/types/task-sources';
import { TrackerIcon } from '@/components/TrackerIcon';
import { Badge } from '@/components/ui/badge';
import { CHIP_COLOURS } from '@/components/ui/chip-colours';
import { cn } from '@/lib/utils';

/**
 * Label chips and member names for tasks (their avatars are MemberAvatar, in components/team). Kept
 * free of the router and stores.
 */

export function memberName(member: TaskMember | undefined | null): string {
  if (!member) return 'Someone';
  return member.displayname?.trim() || member.email;
}

/** Their handle, else their name: for chat, activity and mentions, where it's read often. */
export function memberHandle(member: TaskMember | undefined | null): string {
  return member?.handle?.trim() || memberName(member);
}

/** A task label: a square chip in the colour picked for it (the one chip palette). */
export function LabelChip({ label, className }: { label: TaskLabel; className?: string }) {
  return (
    <Badge shape="square" size="sm" colour={CHIP_COLOURS.includes(label.color) ? label.color : 'gray'} className={cn('max-w-full', className)}>
      <span className="truncate">{label.name}</span>
    </Badge>
  );
}

/**
 * A task's type: a chip in the type's colour, with the shapes mark so it reads apart from
 * the labels. `suggested`: Jev chose it and nobody has confirmed it yet (a sparkle instead, T-17).
 */
export function TypeChip({ type, suggested, className }: { type: Pick<TaskType, 'name' | 'color'>; suggested?: boolean; className?: string }) {
  return (
    <Badge
      shape="square"
      size="sm"
      colour={CHIP_COLOURS.includes(type.color) ? type.color : 'gray'}
      className={cn('max-w-full', className)}
      data-hint={suggested ? `${type.name}, suggested by Jev` : undefined}
    >
      {suggested ? <Sparkles aria-label="Suggested by Jev" /> : <Shapes aria-hidden />}
      <span className="truncate">{type.name}</span>
    </Badge>
  );
}

/**
 * Where a mirrored task comes from (T-15): "GitHub #57", "jflo T-431", "Asana". A muted chip with the
 * synced-in mark; the task page wraps it in a link to the task upstream.
 */
export function UpstreamChip({ upstream, className }: { upstream: Pick<TaskUpstream, 'kind' | 'ref' | 'source_name'>; className?: string }) {
  return (
    <Badge variant="muted" shape="square" size="sm" className={cn('max-w-full', className)} data-hint={`Mirrored from ${upstream.source_name}`}>
      <ArrowDownToLine aria-hidden />
      <span className="truncate">{upstreamBadgeText(upstream)}</span>
    </Badge>
  );
}

/**
 * SF-T-151: the same, as one small mark, so a busy card has one chip fewer. SF-T-207: the source's own
 * logo (GitHub, Asana, Trello, jflo), left of the requester on a Board card and in the task's toolbar, so
 * the tasks that are on a source stand out. Where it's from ("GitHub #57") is the hint, and what a screen
 * reader says.
 */
export function UpstreamMark({ upstream, className }: { upstream: Pick<TaskUpstream, 'kind' | 'ref' | 'source_name'>; className?: string }) {
  const text = `Mirrored from ${upstream.source_name}: ${upstreamBadgeText(upstream)}`;
  return (
    <span className={cn('inline-flex items-center text-muted-foreground', className)} data-hint={text} role="img" aria-label={text}>
      <TrackerIcon kind={upstream.kind} className="size-3.5" />
    </span>
  );
}

const AI_AVATAR_SIZES = { xs: 'size-5 [&_svg]:size-3', sm: 'size-6 [&_svg]:size-3.5' } as const;

/** The AI builder, where a person's avatar would be: Waiting on, the card's faces. */
export function AiBuilderAvatar({ size = 'sm', ring = false, className }: { size?: keyof typeof AI_AVATAR_SIZES; ring?: boolean; className?: string }) {
  return (
    <span
      data-hint={TASK_AI_BUILDER_NAME}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full border border-ai-border bg-ai-soft text-ai-strong',
        AI_AVATAR_SIZES[size],
        ring && 'ring-2 ring-background',
        className
      )}
    >
      <Bot aria-label={TASK_AI_BUILDER_NAME} />
    </span>
  );
}
